'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useSyncExternalStore } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';

export type MobileStorePlatform = 'ios' | 'android';

const APP_STORE_URL =
  process.env.NEXT_PUBLIC_APPSTORE_URL ??
  'https://apps.apple.com/br/app/pierre-controle-de-gastos-ia/id6749781755?l=en-GB';

const PLAY_STORE_URL =
  process.env.NEXT_PUBLIC_PLAYSTORE_URL ??
  'https://play.google.com/store/apps/details?id=io.cloudwalk.pierre&pcampaignid=web_share';

const DISMISSED_KEY = 'pierre:mobile-store-banner-dismissed';
const BANNER_CHANGE_EVENT = 'pierre:mobile-store-banner-change';
let inMemoryDismissed = false;

type DetectionInput = {
  userAgent: string;
  hasReactNativeBridge?: boolean;
  maxTouchPoints?: number;
  platform?: string;
};

type BrowserEnvironmentInput = Pick<
  DetectionInput,
  'userAgent' | 'maxTouchPoints' | 'platform'
>;

const WEBVIEW_USER_AGENT_PATTERN =
  /PierreWebView|; wv\)|\bwv\b|FBAN|FBAV|FB_IAB|Instagram|Line\/|MicroMessenger|TikTok|LinkedInApp|Twitter/i;

function isDesktopClassIPad({
  userAgent,
  maxTouchPoints = 0,
  platform = '',
}: BrowserEnvironmentInput) {
  return (
    /Macintosh/i.test(userAgent) && /Mac/i.test(platform) && maxTouchPoints > 1
  );
}

export function getMobileStorePlatform({
  userAgent,
  maxTouchPoints = 0,
  platform = '',
}: BrowserEnvironmentInput): MobileStorePlatform | null {
  if (/Android/i.test(userAgent)) return 'android';
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios';
  if (isDesktopClassIPad({ userAgent, maxTouchPoints, platform })) {
    return 'ios';
  }

  return null;
}

export function isMobileWebViewUserAgent(
  userAgent: string,
  environment: Pick<DetectionInput, 'maxTouchPoints' | 'platform'> = {},
): boolean {
  if (WEBVIEW_USER_AGENT_PATTERN.test(userAgent)) return true;

  const isIOS =
    /iPhone|iPad|iPod/i.test(userAgent) ||
    isDesktopClassIPad({ userAgent, ...environment });
  const isAppleWebKit = /AppleWebKit/i.test(userAgent);
  const isMobile = /Mobile/i.test(userAgent);
  const isKnownIOSBrowser = /Safari|CriOS|FxiOS|EdgiOS|OPiOS/i.test(userAgent);

  return isIOS && isAppleWebKit && isMobile && !isKnownIOSBrowser;
}

export function shouldShowMobileBrowserStoreBanner({
  userAgent,
  hasReactNativeBridge = false,
  maxTouchPoints,
  platform,
}: DetectionInput): MobileStorePlatform | null {
  if (
    hasReactNativeBridge ||
    isMobileWebViewUserAgent(userAgent, { maxTouchPoints, platform })
  ) {
    return null;
  }

  return getMobileStorePlatform({ userAgent, maxTouchPoints, platform });
}

export function getMobileStoreUrl(platform: MobileStorePlatform) {
  return platform === 'ios' ? APP_STORE_URL : PLAY_STORE_URL;
}

/**
 * Chrome handles Android Intent URLs by opening the installed package and uses
 * browser_fallback_url only when the package is unavailable. iOS keeps the
 * custom scheme until a Universal Link is available.
 */
export function getMobileAppUrl(platform: MobileStorePlatform) {
  if (platform !== 'android') return 'pierre://home';

  return [
    'intent://home#Intent',
    'scheme=pierre',
    'package=io.cloudwalk.pierre',
    `S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_URL)}`,
    'end',
  ].join(';');
}

function isBannerDismissed() {
  if (inMemoryDismissed) return true;

  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function dismissBanner() {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, 'true');
  } catch {
    inMemoryDismissed = true;
    // Storage may be blocked. Still update current render via event dispatch.
  }
  window.dispatchEvent(new Event(BANNER_CHANGE_EVENT));
}

function getBannerSnapshot(): MobileStorePlatform | null {
  if (typeof window === 'undefined' || isBannerDismissed()) {
    return null;
  }

  return shouldShowMobileBrowserStoreBanner({
    userAgent: window.navigator.userAgent,
    hasReactNativeBridge: Boolean(window.ReactNativeWebView),
    maxTouchPoints: window.navigator.maxTouchPoints,
    platform: window.navigator.platform,
  });
}

function subscribeToBannerChanges(onStoreChange: () => void) {
  window.addEventListener(BANNER_CHANGE_EVENT, onStoreChange);
  window.addEventListener('storage', onStoreChange);

  return () => {
    window.removeEventListener(BANNER_CHANGE_EVENT, onStoreChange);
    window.removeEventListener('storage', onStoreChange);
  };
}

export function MobileBrowserStoreBanner() {
  const pathname = usePathname();
  const platform = useSyncExternalStore(
    subscribeToBannerChanges,
    getBannerSnapshot,
    () => null,
  );

  if (!platform) return null;

  const storeName = platform === 'ios' ? 'App Store' : 'Google Play';

  return (
    <aside
      aria-label="Baixar app Pierre"
      className="sticky top-0 z-50 border-b border-white/10 bg-zinc-950/95 px-4 py-3 text-white shadow-lg backdrop-blur supports-[backdrop-filter]:bg-zinc-950/85 md:hidden"
    >
      <div className="mx-auto flex max-w-3xl items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Use o Pierre pelo app</p>
          <p className="text-xs text-zinc-300">
            Abra pelo {storeName} para uma experiência melhor no celular.
          </p>
        </div>
        <Button
          asChild
          size="sm"
          rounded="full"
          className="h-9 bg-white px-4 text-sm font-semibold text-black hover:bg-zinc-200"
        >
          <Link
            href={getMobileStoreUrl(platform)}
            target="_blank"
            rel="noreferrer"
          >
            Baixar
          </Link>
        </Button>
        <button
          type="button"
          aria-label="Fechar banner"
          className="rounded-full p-2 text-zinc-300 hover:bg-white/10 hover:text-white"
          onClick={dismissBanner}
        >
          <X className="size-4" />
        </button>
      </div>
    </aside>
  );
}
